import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select, message } from 'antd'
import CustomBreadcrumb from '../../common/CustomBreadcrumb'
import CommonTable from '../../common/CommonTable';
import CampaignModal from '../../common/CampaignModal';
import {
    aiCampaignsColumns,
    CAMPAIGN_TYPE_FILTER_OPTIONS,
    isWelcomeOnboardingCampaign,
} from './aiCampaignsUtils';
import { ThemeContext } from '../../common/themeContext';
import Loader from '../Loder/loader';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getAllAiCampaignsHandler } from '../../redux/action/getAllAiCampaignsSlice';
import { getSystemCampaignStatusHandler } from '../../redux/action/getSystemCampaignStatusSlice';
import {
    triggerWelcomeCampaignHandler,
    triggerWelcomeCampaignAction,
} from '../../redux/action/triggerWelcomeCampaignSlice';
import {
    triggerComplianceCampaignHandler,
    triggerComplianceCampaignAction,
} from '../../redux/action/triggerComplianceCampaignSlice';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { ChevronIcon, InfoIcon, RestartIcon } from '../../assets/icon';

const AiCampaigns = () => {
    const navigate = useNavigate();
    const {
        canEditCampaigns,
        hasCampaignViewOnly,
        isSuperAdmin,
        PERMISSIONS,
        hasPermission,
    } = useAdminPermissions();

    const showManageCampaign =
        isSuperAdmin || hasPermission(PERMISSIONS.CAMPAIGN_EDIT);
    const showViewCampaign =
        hasCampaignViewOnly ||
        (hasPermission(PERMISSIONS.CAMPAIGN_VIEW) && !showManageCampaign);

    const columns = aiCampaignsColumns(navigate, {
        showManage: showManageCampaign,
        showView: showViewCampaign,
    });

    const { brandId } = useContext(ThemeContext);
    const dispatch = useDispatch();
    const getAllAiCampaignsState = useSelector((state) => state?.getAllAiCampaigns);
    const systemCampaignStatusState = useSelector((state) => state?.getSystemCampaignStatus);
    const triggerWelcomeCampaignState = useSelector((state) => state?.triggerWelcomeCampaign);
    const triggerComplianceCampaignState = useSelector((state) => state?.triggerComplianceCampaign);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignType, setCampaignType] = useState("ALL");
    const [campaignTypeOpen, setCampaignTypeOpen] = useState(false);
    // "welcome" | "compliance" | null — which trigger confirmation modal is open.
    const [triggerType, setTriggerType] = useState(null);
    // Locally hide a card the instant its trigger call succeeds — don't wait on a fresh
    // system-campaign-status GET, which can race ahead of the backend's own processing.
    const [locallyTriggered, setLocallyTriggered] = useState({ welcome: false, compliance: false });
    // The backend creates the triggered campaign asynchronously, so the row isn't in the list
    // yet by the time the immediate refetch below runs — these hold a delayed follow-up refetch.
    const welcomeRefreshTimeoutRef = useRef(null);
    const complianceRefreshTimeoutRef = useRef(null);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: [5, 10, 15, 20],
        className: "order-pagination",
        locale: { items_per_page: "" },
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
    });

	const normalizeCampaignStatus = (status) => {
        if (!status) return status;
        if (typeof status !== 'string') return status;

        const map = {
            HOLD: "Hold",
            ACTIVE: "Active",
            ARCHIVE: "Archive",
            DRAFT: "Draft",
        };

        const trimmed = status.trim();
        if (map[trimmed]) return map[trimmed];

        // Fallback: turn "hold" -> "Hold", "ACTIVE" -> "Active"
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    };

    const normalizeCampaignDuration = (item) => {
        // Prefer backend-provided duration if present.
        if (item?.campaignDuration) return item.campaignDuration;

        const start = item?.campaignStartDate;
        const end = item?.campaignEndDate;
        const isWelcome = isWelcomeOnboardingCampaign(item?.campaignType);

        if (start && isWelcome) {
            return `${moment(start).format('MM/DD/YYYY')} - Ongoing`;
        }
        if (start && end) {
            const startFormatted = moment(start).format('MM/DD/YYYY');
            const endFormatted = moment(end).format('MM/DD/YYYY');
            return `${startFormatted} - ${endFormatted}`;
        }
        return '';
    };

	const listPayload = useMemo(() => {
        return {
            refBrandId: brandId,
            campaignType: !campaignType || campaignType === "ALL" ? null : campaignType,
            status: null,
        };
    }, [brandId, campaignType]);

    const fetchCampaigns = (page = 1, pageSize = pagination.pageSize) => {
        if (!brandId) return;
        window.scrollTo(0, 0);
        let payload = {
            ...listPayload,
            offSet: page,
            limit: pageSize,
        };
        dispatch(getAllAiCampaignsHandler(payload));
    };

    const fetchSystemCampaignStatus = () => {
        if (!brandId) return;
        dispatch(getSystemCampaignStatusHandler({ refBrandId: brandId }));
    };

    useEffect(() => {
        if (!brandId) return;
        fetchCampaigns(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId, campaignType]);

    useEffect(() => {
        if (!brandId) return;
        fetchSystemCampaignStatus();
        setLocallyTriggered({ welcome: false, compliance: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId]);

    // welcomeCampaignExists/complianceCampaignExists === false -> not created yet, show its
    // trigger card. Once a Trigger call itself succeeds, hide that card immediately rather
    // than waiting on a fresh status GET, which can race ahead of the backend's own
    // processing of the trigger.
    const showWelcomeTriggerCard =
        !locallyTriggered.welcome &&
        systemCampaignStatusState?.data?.data?.welcomeCampaignExists === false;
    // Healthcare2U (brandId 1) doesn't support Compliance campaigns at all — hide the card
    // for that brand outright, regardless of what the status API returns.
    const showComplianceTriggerCard =
        Number(brandId) !== 1 &&
        !locallyTriggered.compliance &&
        systemCampaignStatusState?.data?.data?.complianceCampaignExists === false;

    useEffect(() => {
        const items = getAllAiCampaignsState?.data?.data;
        if (!items) return;

        const mapped = items.map((item) => ({
            // Keep key for antd Table stability; fall back to campaignName.
            key: item?.id ?? item?.campaignId ?? item?.campaignName,
            campaignName: item?.campaignName ?? item?.name ?? "",
            campaignType: item?.campaignType ?? item?.type ?? "",
            campaignDuration: normalizeCampaignDuration(item),
            status: normalizeCampaignStatus(item?.status),
            ...item,
        }));

        setCampaigns(mapped);

        setPagination((prev) => ({
            ...prev,
            total: getAllAiCampaignsState?.data?.totalElements ?? 0,
            current: getAllAiCampaignsState?.data?.pageNumber ?? 1	,
            pageSize: getAllAiCampaignsState?.data?.pageSize ?? prev.pageSize,
        }));
    }, [getAllAiCampaignsState]);

    const addProduct = () => {
        navigate("/ai-campaigns/campaign-details", { state: null });
    };

    const handleCampaignTypeChange = (value) => {
        setCampaignType(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handlePageChange = (page, pageSize) => {
        fetchCampaigns(page, pageSize);
    };

    useEffect(() => {
        if (triggerWelcomeCampaignState?.message) {
            message.success(triggerWelcomeCampaignState.message);
            dispatch(triggerWelcomeCampaignAction.triggerWelcomeCampaignInfoReset());
            setLocallyTriggered((prev) => ({ ...prev, welcome: true }));
            fetchSystemCampaignStatus();
            fetchCampaigns(pagination.current, pagination.pageSize);
            // Backend creates the campaign asynchronously — the row may not be in the list
            // yet, so refetch again shortly after as a safety net.
            clearTimeout(welcomeRefreshTimeoutRef.current);
            welcomeRefreshTimeoutRef.current = setTimeout(() => {
                fetchSystemCampaignStatus();
                fetchCampaigns(pagination.current, pagination.pageSize);
            }, 1000);
        }
        if (triggerWelcomeCampaignState?.error) {
            message.error(triggerWelcomeCampaignState.error);
            dispatch(triggerWelcomeCampaignAction.triggerWelcomeCampaignInfoReset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerWelcomeCampaignState]);

    useEffect(() => {
        if (triggerComplianceCampaignState?.message) {
            message.success(triggerComplianceCampaignState.message);
            dispatch(triggerComplianceCampaignAction.triggerComplianceCampaignInfoReset());
            setLocallyTriggered((prev) => ({ ...prev, compliance: true }));
            fetchSystemCampaignStatus();
            fetchCampaigns(pagination.current, pagination.pageSize);
            // Backend creates the campaign asynchronously — the row may not be in the list
            // yet, so refetch again shortly after as a safety net.
            clearTimeout(complianceRefreshTimeoutRef.current);
            complianceRefreshTimeoutRef.current = setTimeout(() => {
                fetchSystemCampaignStatus();
                fetchCampaigns(pagination.current, pagination.pageSize);
            }, 2500);
        }
        if (triggerComplianceCampaignState?.error) {
            message.error(triggerComplianceCampaignState.error);
            dispatch(triggerComplianceCampaignAction.triggerComplianceCampaignInfoReset());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerComplianceCampaignState]);

    useEffect(() => {
        return () => {
            clearTimeout(welcomeRefreshTimeoutRef.current);
            clearTimeout(complianceRefreshTimeoutRef.current);
        };
    }, []);

    const handleTriggerConfirm = () => {
        if (triggerType === "welcome") {
            dispatch(triggerWelcomeCampaignHandler({ refBrandId: brandId }));
        } else if (triggerType === "compliance") {
            dispatch(triggerComplianceCampaignHandler({ refBrandId: brandId }));
        }
        setTriggerType(null);
    };

    return (
        <div className="rightside">
            <div className="rightcontent">
                <p>
                    <strong>Campaign List</strong>
                </p>
                <CustomBreadcrumb
                    items={[{ text: "Home" }, { text: "AI Campaigns" }, { text: "Campaign List", active: true }]}
                />

                {(showWelcomeTriggerCard || showComplianceTriggerCard) && (
                    <div className="trigger-cards-grid">
                        {showWelcomeTriggerCard && (
                            <div className="trigger-campaign-card">
                                <div>
                                    <p className="trigger-campaign-card-title">Welcome Campaign</p>
                                    <p className="trigger-campaign-card-desc">
                                        Automatically send a personalized welcome email to new
                                        users upon registration.
                                    </p>
                                </div>
                                <button
                                    className="primarybtn"
                                    type="button"
                                    onClick={() => setTriggerType("welcome")}
                                >
                                    Trigger
                                </button>
                            </div>
                        )}
                        {showComplianceTriggerCard && (
                            <div className="trigger-campaign-card">
                                <div>
                                    <p className="trigger-campaign-card-title">Compliance Campaign</p>
                                    <p className="trigger-campaign-card-desc">
                                        Trigger compliance reminders and policy acknowledgment
                                        emails
                                    </p>
                                </div>
                                <button
                                    className="primarybtn"
                                    type="button"
                                    onClick={() => setTriggerType("compliance")}
                                >
                                    Trigger
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="table-padding">
                    <div className="headtitle">
                        <p className="mb-0 heedprodct">Campaign List</p>
                        <div className="d-flex align-items-center gap-3">
                            <div className="customSelectInput mb-0" style={{ minWidth: 220 }}>
                                <Select
                                    className="customSelect w-100"
                                    placeholder="Campaign type"
                                    value={campaignType}
                                    onChange={handleCampaignTypeChange}
                                    onDropdownVisibleChange={setCampaignTypeOpen}
                                    options={CAMPAIGN_TYPE_FILTER_OPTIONS}
                                    suffixIcon={<ChevronIcon isOpen={campaignTypeOpen} />}
                                    popupMatchSelectWidth={false}
                                />
                            </div>
                            {canEditCampaigns && Number(brandId) !== 1 && (
                                <button className="primarybtn" type="button" onClick={addProduct}>
                                    <div className="plus"></div>
                                    Create Enrollment Campaign
                                </button>
                            )}
                        </div>
                    </div>
                    <hr className="m-0" />
                    <div className="table-responsive">
                        {getAllAiCampaignsState?.isLoading === true && (
                            <Loader loaderTransform="loaderTransform" />
                        )}
                        <CommonTable
                            handlePageChange={handlePageChange}
                            pagination={pagination}
                            body={campaigns}
                            columns={columns}
                        />
                    </div>
                </div>
            </div>

            <CampaignModal
                open={!!triggerType}
                onCancel={() => setTriggerType(null)}
                onAction={handleTriggerConfirm}
                title={triggerType === "compliance" ? "Trigger Compliance Campaign" : "Trigger Welcome Campaign"}
                subtitle="Activate this campaign and start AI Agent outreach"
                actionLabel="Trigger Campaign"
                actionIcon={<RestartIcon color="#fff" />}
            >
                <div className="campaign-modal-info-box">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <InfoIcon />
                        <strong className="campaign-modal-info-title">
                            This action will:
                        </strong>
                    </div>
                    <p className="campaign-modal-info-text mb-0">
                        Activate this campaign for the first time and start AI Agent
                        outreach. This can only be done once.
                    </p>
                </div>
            </CampaignModal>
        </div>
    )
}

export default AiCampaigns
